import { useNavigate } from "react-router-dom";
import Markdown from "react-markdown";
import { AlertCircle, ArrowLeft, Settings } from "lucide-react";

interface DataActPrerequisiteGateProps {
  productId: string;
  productName?: string;
  /**
   * Current value of the `product-type` tag, if any. Used to tailor the copy
   * between "not set" and "not applicable for this assessment".
   */
  currentProductType?: string | undefined;
  /** Optional guidance text pulled from Q1.1 of the questionnaire definition. */
  guidance?: string | undefined;
}

export default function DataActPrerequisiteGate({
  productId,
  productName,
  currentProductType,
  guidance,
}: DataActPrerequisiteGateProps) {
  const navigate = useNavigate();

  const isSetButIneligible = !!currentProductType;

  const title = isSetButIneligible
    ? "Product type not applicable"
    : "Product type required";

  const body = isSetButIneligible
    ? `The Data Act assessment applies only to products classified as **hardware** or **software**. This product is currently set to **${currentProductType}**, which falls outside the scope of this assessment.\n\nUpdate the product record if this classification is incorrect.`
    : "To proceed with this assessment, we need to confirm whether this product is **hardware** or **software**. This determines which section of the EU Data Act questionnaire applies.\n\nSet this in the product record and come back.";

  const handleGoToEdit = () => {
    navigate(
      `/products/${productId}?edit=true&focus=product-type&resumeAfter=data-act`,
    );
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 mt-2 flex-1 flex items-center justify-center">
      <div className="text-center max-w-xl px-6 py-8">
        <div className="w-20 h-20 rounded-full bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center mx-auto mb-5">
          <AlertCircle className="h-10 w-10 text-amber-600 dark:text-amber-400" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
          {title}
        </h3>
        {productName && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {productName}
          </p>
        )}
        <div className="prose prose-sm dark:prose-invert max-w-none text-gray-600 dark:text-gray-300 mb-6 text-left">
          <Markdown>{body}</Markdown>
        </div>
        {guidance && (
          <details className="mb-6 text-left" open>
            <summary className="cursor-pointer text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline">
              Why is this question asked?
            </summary>
            <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800/30 rounded-md">
              <h5 className="text-xs font-semibold text-blue-800 dark:text-blue-300 uppercase tracking-wide mb-1.5">
                Guidance
              </h5>
              <Markdown
                components={{
                  p: ({ children }) => (
                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-2 last:mb-0">
                      {children}
                    </p>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-semibold text-gray-900 dark:text-gray-100">
                      {children}
                    </strong>
                  ),
                  ul: ({ children }) => (
                    <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-300 mb-2 last:mb-0">
                      {children}
                    </ul>
                  ),
                  li: ({ children }) => (
                    <li className="leading-relaxed">{children}</li>
                  ),
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300"
                    >
                      {children}
                    </a>
                  ),
                }}
              >
                {guidance}
              </Markdown>
            </div>
          </details>
        )}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={() => navigate(`/products/${productId}`)}
            className="btn btn-outline text-sm w-full sm:w-auto"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Product
          </button>
          <button
            onClick={handleGoToEdit}
            className="btn btn-primary text-sm w-full sm:w-auto"
          >
            <Settings className="h-4 w-4 mr-1" />
            Set Product Type
          </button>
        </div>
      </div>
    </div>
  );
}
